"use client";
// Requirements summary:
// - Coach + Parent persona toggle: a single auth user can be both staff (coach)
//   AND parent at the same org. This context detects all applicable personas
//   for the currently selected org and exposes:
//     * availablePersonas: which dashboards are accessible at this org
//     * activePersona: which dashboard the user is currently viewing
//     * setActivePersona: switches between dashboards (per-org persistence)
//   The toggle is shown in the UI only when availablePersonas.length > 1.
//   For any staff member, availablePersonas includes 'parent' (so they can use
//   the parent dashboard to add their own kids), even before they have any
//   parent activity at this gym. Empty state on the parent dashboard handles
//   the no-kids-yet case.
// - userType is derived from activePersona so all existing code paths that
//   check userType ('staff' vs 'parent' vs 'athlete') continue to work
//   without changes — flipping persona is transparent to consumers.
// - Permissions are gated by activePersona: when in parent persona, all
//   permissions are false (hard separation, no hybrid view).
// - Same-tab account switching must reload role/profile data even when the
//   selected gym is unchanged, so one parent cannot inherit another parent's
//   dropdown profile.
// - Plan 2026-05-23-002 (U1): same-account dual-persona (athlete + parent on
//   one login) was removed before its first production rollout. The feature
//   flag had stayed OFF, so the surface area is being deleted to simplify
//   this file before the unified account-holder migration ships. Only the
//   pre-existing Coach + Parent toggle infrastructure remains here.
// - Family / Guardian Access U4 (R14): STAFF CONTEXT IS DELIBERATELY SEPARATE
//   FROM FAMILY CONTEXT. This context still answers only "what staff role /
//   dashboard persona am I", and userPermissions stays derived purely from the
//   active staff role. The active FAMILY (which household a parent is viewing)
//   lives in the separate FamilyContext provider and is NOT read here. Switching
//   family must never change staff permissions, and switching persona/role must
//   never change family membership. FamilyContext only fetches in the parent
//   persona (userType === 'parent'), so a coach on the staff dashboard never
//   carries a family scope. Do not couple these two contexts.
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { supabase } from '@/utils/supabase/client';
import { getSelfManagedAthleteIds } from '@/utils/relationships/parentAthleteRelationships';
import { OrgRole, DEFAULT_ORG_PERMISSIONS, RoleUtils } from '@/utils/constants/roles';
import { createLogger } from '@/utils/logger/devLogger';
import { FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY } from '@/utils/auth/forceStaffPersona';
import {
  resolveActiveStaffRoleId,
  resolveAssignedStaffRoleFlags,
} from '@/utils/auth/resolveAssignedStaffRoleFlags';
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
} from '@/utils/storage/safeLocalStorage';

// Coach + Parent persona toggle: a single auth user can be both staff (coach)
// AND parent at the same org. After the U1 cleanup above, 'athlete' is no
// longer a runtime persona option — only 'coach' and 'parent' resolve.
export type DashboardPersona = 'coach' | 'parent';

function resolveDashboardPersonas({
  hasStaffAtOrg,
  hasParentMembershipAtOrg,
}: {
  hasStaffAtOrg: boolean;
  hasParentMembershipAtOrg: boolean;
}): DashboardPersona[] {
  return hasStaffAtOrg
    ? ['coach', 'parent']
    : hasParentMembershipAtOrg
    ? ['parent']
    : [];
}

// Unit 5 fix wave: small local interface for the auth_users select chains used
// throughout this file. The supabase typed client otherwise narrows these to
// generated row shapes that are noisy here, so we treat them locally with a
// strict shape and avoid `as any` casts at each call site.
interface AuthUserProfileFields {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  user_type?: string | null;
}

const logger = createLogger('UserRoleContext');

// localStorage key pattern for per-org persona persistence. Mirrors the
// existing activeRole_${userId}_${orgId} pattern.
const personaStorageKey = (userId: string, orgId: string) =>
  `activePersona_${userId}_${orgId}`;

// User profile interface
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatar_url?: string; // ✅ ADDED: Include avatar URL from auth_users table
  role?: string;
  activeRole: OrgRole | null;
  userType: 'staff' | 'parent' | 'athlete' | 'unknown';
  /** Athlete logins only: this adult manages their own account (#5413 one rule). */
  isSelfManaged?: boolean;
  // Coach + Parent persona toggle:
  availablePersonas: DashboardPersona[]; // [] for athletes/unknown, ['coach'|'parent'] or both
  activePersona: DashboardPersona | null; // null for athletes/unknown
}

// User permissions interface
export interface UserPermissions {
  can_manage_staff: boolean;
  can_manage_teams: boolean;
  can_manage_athletes: boolean;
  can_manage_billing: boolean;
  can_manage_locations: boolean;
  can_view_reports: boolean;
  can_send_notifications: boolean;
  can_manage_notifications: boolean;
  can_manage_notification_templates: boolean;
}

// User role context interface
export type RoleResolution = 'loading' | 'resolved' | 'unconfirmed';

interface UserRoleContextType {
  currentUser: UserProfile | null;
  availableRoles: OrgRole[];
  userPermissions: UserPermissions;
  setActiveRole: (role: OrgRole) => void;
  // Coach + Parent persona toggle: programmatic switch between coach and
  // parent dashboards. The visible toggle UI was removed when the unused
  // same-account dual-persona surface was deleted, but the underlying state
  // API is still used by guards like StaffRegistrationGuard to route a
  // signed-in coach to their own family dashboard. `redirectTo` lands the
  // switch on a specific page of the new persona instead of its dashboard root.
  setActivePersona: (persona: DashboardPersona, options?: { redirectTo?: string }) => void;
  isLoading: boolean;
  error: string | null;
  /**
   * Whether we actually know who this viewer is.
   *
   * 'unconfirmed' is set ONLY where resolution genuinely failed — it is not
   * inferred from a null currentUser, because several ordinary states leave
   * that null without meaning "we could not check". Public self-service pages
   * treat it as a block, so widening it silently would stop real parents
   * enrolling.
   */
  roleResolution: RoleResolution;
}

// Create context with default values
const UserRoleContext = createContext<UserRoleContextType>({
  currentUser: null,
  availableRoles: [],
  userPermissions: {
    can_manage_staff: false,
    can_manage_teams: false,
    can_manage_athletes: false,
    can_manage_billing: false,
    can_manage_locations: false,
    can_view_reports: false,
    can_send_notifications: false,
    can_manage_notifications: false,
    can_manage_notification_templates: false,
  },
  setActiveRole: () => {},
  setActivePersona: () => {},
  isLoading: false,
  error: null,
  roleResolution: 'resolved',
});

// Create provider component
export function UserRoleProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [availableRoles, setAvailableRoles] = useState<OrgRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set only where role resolution genuinely failed; see RoleResolution.
  const [resolutionFailed, setResolutionFailed] = useState(false);

  const { user, isLoading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading, isSwitchingOrganization } = useOrganization();
  // Coach + Parent persona toggle: used by setActivePersona to route the
  // user to the appropriate dashboard root after switching, so they don't
  // land on a coach-only page (e.g. /dashboard/teams/123) while in parent
  // persona — which would trigger the existing "Access Denied" guards.
  const router = useRouter();

  // Track the organization and auth user we've loaded roles for to detect stale data
  const [loadedOrgId, setLoadedOrgId] = useState<string | null>(null);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  // Issue #978: the LIVE per-org `org_staff_roles.can_manage_athletes` boolean for the
  // ACTIVE staff role. We override the compile-time DEFAULT_ORG_PERMISSIONS value with
  // this so the UI's read-only/edit decision agrees with RLS per-org (a gym that, via
  // #471, re-enables coach editing sees the edit buttons reappear with no code change).
  // null = no live override available (non-staff, or role not resolved yet) → fall back
  // to the constant. SCOPED to just this capability; the broader constant-vs-DB
  // reconciliation for the other flags is a tracked follow-up.
  const [activeRoleCanManageAthletes, setActiveRoleCanManageAthletes] = useState<boolean | null>(null);
  // U9 (R19): live per-org send revocation flag for the signed-in staff member.
  // true → hide compose (can_send_notifications forced false). null → constant.
  const [sendingDisabledForUser, setSendingDisabledForUser] = useState<boolean | null>(null);

  // Coach + Parent persona toggle: counter we bump from setActivePersona to
  // force the bridge useEffect to re-run via its dependency array. Kept
  // separate from loadedOrgId so the existing org-switch logic stays intact.
  const [personaRefreshTick, setPersonaRefreshTick] = useState(0);

  // Per-invocation generation token. Every call to fetchUserRoles increments
  // this and captures the value locally; before each setState the function
  // compares against the current generation and returns early if a newer
  // fetch has started. Prevents an in-flight fetch from clobbering newer
  // state on rapid persona/org switches.
  const fetchRolesGenerationRef = useRef(0);
  // `${userId}|${orgId}` the latest run (finished or still in flight) was for.
  const latestRunTargetRef = useRef<string | null>(null);

  // Calculate permissions based on active role
  const getUserPermissions = (role: OrgRole | null): UserPermissions => {
    if (!role) {
      return {
        can_manage_staff: false,
        can_manage_teams: false,
        can_manage_athletes: false,
        can_manage_billing: false,
        can_manage_locations: false,
        can_view_reports: false,
        can_send_notifications: false,
        can_manage_notifications: false,
        can_manage_notification_templates: false,
      };
    }
    return DEFAULT_ORG_PERMISSIONS[role] || {
      can_manage_staff: false,
      can_manage_teams: false,
      can_manage_athletes: false,
      can_manage_billing: false,
      can_manage_locations: false,
      can_view_reports: false,
      can_send_notifications: false,
      can_manage_notifications: false,
      can_manage_notification_templates: false,
    };
  };

  // Fetch user roles from database. The generation token prevents an
  // in-flight fetch from clobbering newer state when the user switches
  // organizations rapidly.
  const fetchUserRoles = async () => {
    // Skip redundant fetch when roles are already loaded for this org and auth user.
    // Guards against the demo mode race condition where refreshOrganizations() inside
    // switchToDemoOrg toggles orgLoading (true→false), re-firing this effect with the
    // OLD org still set. Without this check, that stale call's setLoadedOrgId would
    // overwrite the demo org's loadedOrgId, leaving isRolesStale=true permanently.
    const hasPendingStaffPersonaRedirect =
      safeSessionStorageGetItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY) === 'true';

    // #3392: a FAILED resolution is never "already loaded". The mobile webview
    // resolves once against the minimal org stub (no `billingOnly` flag) and
    // fails; when the flagged org lands moments later this guard used to skip
    // the re-fetch, freezing the failure until a manual retry.
    // Under the demo-switch orgLoading toggle above, this carve-out stays safe
    // only because of the generation token: the stale re-fired effect's extra
    // fetch loses the setLoadedOrgId race to the newer generation, so letting
    // it through here cannot resurrect the overwrite this guard exists to stop.
    //
    // Checked BEFORE the generation token is taken (#5726): a skip that took a
    // token would supersede a run still in flight for this same user and gym —
    // e.g. a #3392 retry that has already cleared the flag — and that run's
    // `finally` would then never turn loading off. It skips only when the
    // latest run was for this same user and gym, so a run for anything else
    // (e.g. a no-gym run started while the gym was briefly cleared) is still
    // superseded by a fresh fetch here.
    if (
      user &&
      currentOrganization &&
      !isSwitchingOrganization &&
      latestRunTargetRef.current === `${user.id}|${currentOrganization.id}` &&
      loadedOrgId === currentOrganization.id &&
      loadedUserId === user.id &&
      !hasPendingStaffPersonaRedirect &&
      !resolutionFailed
    ) {
      // Same clears every run made before this check moved up (#978 / U9).
      setActiveRoleCanManageAthletes(null);
      setSendingDisabledForUser(null);
      logger.info('User roles already loaded for current org and user, skipping fetch', {
        orgId: currentOrganization.id,
        userId: user.id,
      });
      return;
    }

    const generation = ++fetchRolesGenerationRef.current;
    const isCurrent = () => generation === fetchRolesGenerationRef.current;
    latestRunTargetRef.current = `${user?.id ?? ''}|${currentOrganization?.id ?? ''}`;

    // Issue #978: start every fetch with no live can_manage_athletes override so a
    // stale value can't bleed across an org/user/persona switch. Only the staff
    // coach-persona branch below sets it to the live per-org boolean; every other
    // resolution path (athlete, parent, org-less, no-roles) correctly falls back to
    // the DEFAULT_ORG_PERMISSIONS constant.
    setActiveRoleCanManageAthletes(null);
    setSendingDisabledForUser(null);

    if (!user) {
      if (!isCurrent()) return;
      setCurrentUser(null);
      setAvailableRoles([]);
      setLoadedUserId(null);
      setIsLoading(false);
      return;
    }

    // Handle organization-less users (self-registered parents/athletes from Central Signup flow)
    if (!currentOrganization) {
      // The failure flag is cleared only where an answer is committed below, not
      // here: clearing it before the reads would let a viewer whose last check
      // failed read as resolved while this run is still in flight.
      // No gym selected: confirm whether this account is staff before anything
      // else. Staff users need to be identified immediately so enrollment
      // guards block them (there's no org selection modal on public pages).
      // This runs whether or not the gym list is empty (#3196): the list comes
      // back empty when its own read fails, and it is built from the account
      // type, so a parent-type account holding a staff row can have none.
      // Every coach also has a parents row, so the lookup below would read
      // them as a parent. Parents/athletes fall through to the org-less
      // resolution below — see the note on that branch for why they must NOT
      // wait here.
      logger.info('No gym selected, checking user type...');

      const { data: authUserCheck, error: authUserCheckError } = await supabase
        .from('auth_users')
        .select('first_name, last_name, avatar_url, user_type')
        .eq('id', user.id)
        .maybeSingle<AuthUserProfileFields>();

      const userCheck = authUserCheck;

      // Staff classification decides whether the public enrollment guard fires
      // (`currentUser?.userType === 'staff'`), and that guard is CLIENT-ONLY —
      // the enrollment routes do not re-check user_type. So this must not fail
      // open. Two ways it could:
      //
      //  1. The read failed. Before the fall-through below existed, an errored
      //     read left the user hanging, which blocked them by accident. Now an
      //     unresolved read would sail through as 'parent'. Treat an errored
      //     read as unconfirmed — worst case a real parent sees the catalog
      //     with the enroll button withheld until the next load, which is
      //     recoverable; the reverse lets a coach self-enroll. A MISSING row is
      //     not an error: it never appears on retry (19 accounts in prod on
      //     2026-10-05, none holding a staff row), so it goes on to the staff
      //     probe below like any non-staff type rather than to a dead-end retry.
      //  2. `auth_users.user_type` is one denormalized column and it disagrees
      //     with reality for 46 users in prod (2026-10-05: user_type='parent'
      //     holding an active organization_staff row). Confirm
      //     against the staff table itself, which the user can always read for
      //     their own rows (RLS: select_own_organization_staff_record).
      let resolvedAsStaff = userCheck?.user_type === 'gym_staff';
      // Distinct from "not staff": we could not find out. Callers block on it
      // rather than guessing, so we never tell a parent they are a coach.
      let couldNotConfirm = false;

      if (!resolvedAsStaff) {
        if (authUserCheckError) {
          logger.warn('Could not confirm account type — read failed', authUserCheckError);
          couldNotConfirm = true;
        } else {
          if (!userCheck) logger.warn('No auth_users row for this user — checking staff membership directly');
          const { data: staffRow, error: staffRowError } = await supabase
            .from('organization_staff')
            .select('auth_user_id')
            .eq('auth_user_id', user.id)
            .eq('status', 'active')
            .limit(1)
            .maybeSingle();

          if (staffRowError) {
            logger.warn('Could not confirm staff membership', staffRowError);
            couldNotConfirm = true;
          } else if (staffRow) {
            logger.info('user_type is not gym_staff but an active staff row exists — resolving as staff');
            resolvedAsStaff = true;
          }
        }
      }

      if (couldNotConfirm) {
        // Fail closed WITHOUT inventing an identity. Self-service pages block
        // on this and offer a retry; browsing is unaffected.
        if (!isCurrent()) return;
        setCurrentUser(null);
        setAvailableRoles([]);
        setResolutionFailed(true);
        setError('We could not confirm your account');
        setLoadedOrgId(null);
        setLoadedUserId(user.id);
        setIsLoading(false);
        return;
      }

      if (resolvedAsStaff) {
        // Staff on a public page without org selected — set staff profile
        // so enrollment guards block them immediately.
        logger.info('Staff user with no gym selected, setting staff profile');

        const staffProfile: UserProfile = {
          id: user.id,
          email: user.email || '',
          firstName: (userCheck && userCheck.first_name) || user.user_metadata?.first_name || '',
          lastName: (userCheck && userCheck.last_name) || user.user_metadata?.last_name || '',
          avatar_url: (userCheck && userCheck.avatar_url) || undefined,
          activeRole: null,
          userType: 'staff',
          // Org-less holding state — persona toggle not applicable yet.
          availablePersonas: [],
          activePersona: null,
        };
        if (!isCurrent()) return;
        setCurrentUser(staffProfile);
        setAvailableRoles([]);
        setLoadedOrgId(null);
        setLoadedUserId(user.id);
        setResolutionFailed(false);
        setIsLoading(false);
        logger.info('Org-pending staff profile created:', { userProfile: staffProfile });
        return;
      }

      // Parents/athletes fall through to the org-less resolution below.
      //
      // For a multi-gym viewer this check used to stop here and hold isLoading true forever,
      // waiting for the gym picker. That deadlocks a multi-gym parent on
      // every PUBLIC page: the picker is mounted inside the admin layout
      // (OrganizationSelectionWrapper) only, so on a public program catalog,
      // enrollment, waitlist, or booking page there is nothing to pick with
      // and the page sits on "Checking your account..." until reload. A gym
      // slug in the URL already says whose catalog is being viewed, so
      // browsing never needed the viewer's own gym resolved at all.
      //
      // Falling through is safe on admin pages too: OrganizationSelectionWrapper
      // renders the picker INSTEAD of its children while a selection is
      // pending, so no admin surface reads this org-less profile. Once a gym
      // is picked, currentOrganization?.id changes and this effect re-runs to
      // resolve the org-scoped role.
      logger.info('Parent/athlete with no gym selected — resolving org-less so public pages can render');

      logger.info('Creating basic user profile for organization-less user');

      // Plan 2026-05-23-002 U2/U6: lookup order — check for a parents row BEFORE
      // the athletes.auth_user_id self-record. A user with BOTH (the new
      // dual-shape created by U3's backfill for previously-independent adult
      // athletes) must resolve as userType='parent' and land on the unified
      // parent dashboard. A user with ONLY an athlete row (legacy
      // self-managed athlete not yet backfilled) still resolves as
      // userType='athlete' and is routed by /dashboard/page.tsx to the unified
      // dashboard surface (/dashboard/athlete subtree retired in U6).
      //
      // PR #544 remediation note (ADV-001): unlike the org-scoped branch
      // below, this org-less context has no current org to scope against.
      // The parents-row probe here intentionally checks "does this auth
      // user have at least one parent role anywhere in CheerVue?" — if so,
      // the parent dashboard is the right default landing. A cross-gym
      // user with parent role at Gym B and athlete role at Gym A will
      // land on the parent dashboard initially; when they later select
      // Gym A from the org switcher, the org-scoped branch will re-resolve
      // them correctly as an athlete at Gym A (no parent_organizations row
      // there).
      const [parentsRowResult, athleteDataResult] = await Promise.all([
        supabase
          .from('parents')
          .select('id')
          .eq('auth_user_id', user.id)
          .maybeSingle(),
        supabase
          .from('athletes')
          .select('id, first_name, last_name, profile_image_url, auth_user_id')
          .eq('auth_user_id', user.id)
          .maybeSingle(),
      ]);

      const parentsRow = parentsRowResult.data;
      const athleteData = athleteDataResult.data;

      // Athlete-only path: no parents row → an athlete login that does not
      // manage its own account (#5413).
      // (Dual-shape users with both rows fall through to the
      // parents/auth_users branch below, which routes them to the parent
      // dashboard.)
      if (athleteData && !parentsRow) {
        logger.info('Found athlete login without organization (no parents row):', {
          athleteId: athleteData.id,
        });

        const athleteProfile: UserProfile = {
          id: user.id,
          email: user.email || '',
          firstName: athleteData.first_name || user.user_metadata?.first_name || '',
          lastName: athleteData.last_name || user.user_metadata?.last_name || '',
          avatar_url: athleteData.profile_image_url || undefined,
          activeRole: null, // No organization means no org role
          userType: 'athlete',
          // No parents row, so no self row either: never self-managed (#5413).
          isSelfManaged: false,
          // Athletes have their own dashboard — no persona toggle.
          availablePersonas: [],
          activePersona: null,
        };

        if (!isCurrent()) return;
        setCurrentUser(athleteProfile);
        setAvailableRoles([]);
        setLoadedOrgId(null);
        setLoadedUserId(user.id);
        setResolutionFailed(false);
        setIsLoading(false);
        logger.info('Organization-less athlete profile created:', { userProfile: athleteProfile });
        return;
      }

      // Staff (by account type or by staff row) already returned above.
      const userData = userCheck;

      // Otherwise, treat as parent (self-registered parent from Central Signup)
      const userProfile: UserProfile = {
        id: user.id,
        email: user.email || '',
        firstName: (userData && userData.first_name) || user.user_metadata?.first_name || '',
        lastName: (userData && userData.last_name) || user.user_metadata?.last_name || '',
        avatar_url: (userData && userData.avatar_url) || undefined,
        activeRole: null, // No organization means no org role
        userType: 'parent', // Self-registered users are typically parents
        // Org-less self-registered parent — parent dashboard, no toggle.
        availablePersonas: ['parent'],
        activePersona: 'parent',
      };

      if (!isCurrent()) return;
      setCurrentUser(userProfile);
      setAvailableRoles([]);
      setLoadedOrgId(null);
      setLoadedUserId(user.id);
      setResolutionFailed(false);
      setIsLoading(false);
      logger.info('Organization-less parent profile created:', { userProfile });
      return;
    }

    // Prevent queries during organization switching
    if (isSwitchingOrganization) {
      logger.info('Organization switching in progress, skipping user role fetch');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      // Deliberately NOT cleared at the top of this function: a run that clears
      // the flag and then early-returns (already-loaded skip, org switch in
      // progress) would leave a null currentUser reading as 'resolved', which
      // useSelfServiceAccess treats as allowed. Clear it only here, where we
      // are committed to producing a fresh answer.
      setResolutionFailed(false);
      
      logger.info('Fetching user roles', { 
        userId: user.id, 
        organizationId: currentOrganization.id 
      });

      // Coach + Parent persona toggle: query BOTH staff and parent membership
      // in parallel for the current org. We need the parent check even when
      // the user is staff, because every coach has a parents row (via DB
      // trigger) and may have parent_organizations at this gym for their kid.
      const [staffResult, parentMembershipResult] = await Promise.all([
        supabase
          .from('organization_staff')
          .select(`
            staff_role_id,
            sending_disabled,
            org_staff_roles(
              id,
              role_name,
              display_name,
              hierarchy_level,
              can_manage_athletes
            )
          `)
          .eq('auth_user_id', user.id)
          .eq('organization_id', currentOrganization.id)
          .eq('status', 'active'),
        supabase
          .from('parent_organizations')
          .select(`
            *,
            parents!inner(auth_user_id)
          `)
          .eq('organization_id', currentOrganization.id)
          .eq('status', 'active')
          .eq('parents.auth_user_id', user.id),
      ]);

      const { data: staffData, error: staffError } = staffResult;
      const { data: parentMembershipData, error: parentMembershipError } = parentMembershipResult;

      if (staffError) {
        logger.error('Error fetching user roles:', staffError);
        throw new Error(`Failed to fetch user roles: ${staffError.message}`);
      }
      if (parentMembershipError) {
        // Non-fatal — log and continue with staff-only view if parent query fails.
        logger.warn('Error fetching parent membership; continuing with staff data only', parentMembershipError);
      }

      const hasStaffAtOrg = !!(staffData && staffData.length > 0);
      const hasParentMembershipAtOrg = !!(parentMembershipData && parentMembershipData.length > 0);

      // #1354: dedicated demo-viewer access. A real user browsing the shared demo
      // gym holds a public.demo_org_viewers grant instead of an organization_staff
      // row, so the staff/parent queries above find nothing. Resolve them as a
      // read-only owner-equivalent so the demo dashboard and nav render exactly as
      // before. STRICTLY gated on is_demo, so real-org role resolution is never
      // affected. The shared demo org is read-only (client useDemoGuard + server
      // rejectDemoWrite), so owner-level UI capability here cannot mutate demo data.
      if (!hasStaffAtOrg && !hasParentMembershipAtOrg && currentOrganization.is_demo === true) {
        const { data: demoGrant } = await supabase
          .from('demo_org_viewers')
          .select('demo_org_id')
          .eq('auth_user_id', user.id)
          .eq('demo_org_id', currentOrganization.id)
          .maybeSingle();

        if (demoGrant) {
          const { data: demoAuthUserData } = await supabase
            .from('auth_users')
            .select('first_name, last_name, avatar_url')
            .eq('id', user.id)
            .single<AuthUserProfileFields>();

          // Demo viewers are a staff-shell-only persona: there is no parent
          // relationship in the demo, so no persona toggle is offered.
          const demoUserProfile: UserProfile = {
            id: user.id,
            email: user.email || '',
            firstName: (demoAuthUserData && demoAuthUserData.first_name) || '',
            lastName: (demoAuthUserData && demoAuthUserData.last_name) || '',
            avatar_url: (demoAuthUserData && demoAuthUserData.avatar_url) || undefined,
            activeRole: 'owner' as OrgRole,
            userType: 'staff',
            availablePersonas: ['coach'],
            activePersona: 'coach',
          };

          if (!isCurrent()) return;
          setCurrentUser(demoUserProfile);
          setAvailableRoles(['owner' as OrgRole]);
          // Owner-level UI so the full demo browse renders; demo writes are blocked
          // at both the client (useDemoGuard) and API (rejectDemoWrite) layers.
          setActiveRoleCanManageAthletes(true);
          setLoadedOrgId(currentOrganization.id);
          setLoadedUserId(user.id);
          logger.info('Demo-viewer profile created (read-only owner-equivalent)', {
            userId: user.id,
            organizationId: currentOrganization.id,
          });
          return;
        }
      }

      // Extract available roles before resolving personas so saved roles and
      // the active staff role remain consistent after persona switches.
      const roles: OrgRole[] = (staffData || [])
        .map(staffRecord => {
          // staffData rows ship the joined org_staff_roles in either object
          // or array shape depending on the supabase-js join cardinality
          // hint; both are handled here without an `as any` cast.
          const joined = (staffRecord as { org_staff_roles?: { role_name?: string } | Array<{ role_name?: string }> }).org_staff_roles;
          if (Array.isArray(joined)) return joined[0]?.role_name;
          return joined?.role_name;
        })
        .filter((role): role is string => !!role && RoleUtils.isOrgRole(role))
        .filter((role, index, self) => self.indexOf(role) === index) as OrgRole[]; // Remove duplicates

      // Additional safety: Use Set for guaranteed deduplication
      const uniqueRoles = [...new Set(roles)] as OrgRole[];

      // Compute personas. All staff get an empty-state family persona even
      // before they add a child. The toggle UI hides itself when length < 2.
      const computedPersonas = resolveDashboardPersonas({
        hasStaffAtOrg,
        hasParentMembershipAtOrg,
      });

      // Read saved persona preference for this user+org pair, validate it
      // against what's actually available (defends against stale localStorage
      // entries from before role changes).
      let activePersona: DashboardPersona | null = null;
      let didForceStaffPersonaAfterLogin = false;
      if (computedPersonas.length > 0) {
        const savedPersona = localStorage.getItem(personaStorageKey(user.id, currentOrganization.id)) as DashboardPersona | null;
        const forceStaffPersonaAfterLogin = safeSessionStorageGetItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY) === 'true';

        if (forceStaffPersonaAfterLogin && hasStaffAtOrg && computedPersonas.includes('coach')) {
          // Direct URL login safety: if a signed-out user opens a staff-style
          // athlete URL, logs in as a gym staff account, and had parent mode
          // saved from a prior visit, render the staff shell for this first
          // load. Staff can still switch to parent mode intentionally after.
          activePersona = 'coach';
          didForceStaffPersonaAfterLogin = true;
          safeSessionStorageRemoveItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY);
        } else if (savedPersona && computedPersonas.includes(savedPersona)) {
          if (forceStaffPersonaAfterLogin && !hasStaffAtOrg) {
            safeSessionStorageRemoveItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY);
          }
          activePersona = savedPersona;
        } else {
          if (forceStaffPersonaAfterLogin && !hasStaffAtOrg) {
            safeSessionStorageRemoveItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY);
          }
          // Default to 'coach' when both available (preserves existing
          // behavior for users who were previously coach-only and avoids
          // surprising them with parent view on first load).
          activePersona = computedPersonas[0];
        }
      } else if (safeSessionStorageGetItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY) === 'true') {
        // The force flag is one-time state. If this account has no dashboard
        // persona for the selected org, clear it so it cannot affect a later
        // login in the same tab.
        safeSessionStorageRemoveItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY);
      }

      // If user has staff roles, handle as staff member
      if (hasStaffAtOrg) {
        logger.info('Available staff roles before deduplication:', roles);
        logger.info('Available staff roles after deduplication:', uniqueRoles);
        
        if (uniqueRoles.length === 0) {
          logger.warn('No valid staff roles found after filtering');
          if (!isCurrent()) return;
          setCurrentUser(null);
          setAvailableRoles([]);
          setLoadedOrgId(currentOrganization.id);
          setLoadedUserId(user.id);
          setError('No valid roles found for this user');
          // Sets loadedOrgId, so isRolesStale will NOT hold this at 'loading' —
          // flag it explicitly or a null-user viewer reads as allowed (#3200).
          setResolutionFailed(true);
          return;
        }

        // Get saved active role or use first available
        const savedRoleKey = `activeRole_${user.id}_${currentOrganization.id}`;
        const savedRole = localStorage.getItem(savedRoleKey);
        const activeRole = (savedRole && uniqueRoles.includes(savedRole as OrgRole))
          ? (savedRole as OrgRole)
          : uniqueRoles[0];

        // Issue #978 + #1058: capture the LIVE per-org can_manage_athletes boolean for
        // the ACTIVE staff role from the joined org_staff_roles row, so the UI honors
        // the same per-org decision RLS enforces (instead of the compile-time constant).
        // Disambiguate by the assigned role-definition id (organization_staff
        // .staff_role_id → org_staff_roles.id) — the SAME key RLS uses — rather than by
        // role_name. When a gym has two role-defs sharing a role_name but different
        // capability flags, name-matching could read the wrong def's flag (#1058).
        const activeStaffRoleId = resolveActiveStaffRoleId(staffData, activeRole);
        const { canManageAthletes: liveCanManageAthletes } = resolveAssignedStaffRoleFlags(
          staffData,
          activeStaffRoleId
        );

        // Get user profile data from auth_users if available
        const { data: authUserData } = await supabase
          .from('auth_users')
          .select('first_name, last_name, avatar_url') // ✅ ADDED: Include avatar_url
          .eq('id', user.id)
          .single<AuthUserProfileFields>();

        // Coach + Parent persona toggle: when the user is in parent persona,
        // we present them as a parent — userType flips, activeRole clears,
        // and availableRoles becomes [] so userPermissions resolves to all
        // false. This enforces the hard-separation contract: a coach in
        // parent view has zero coach capabilities in the UI.
        const isInCoachPersona = activePersona === 'coach';
        if (!isCurrent()) return;
        if (!isInCoachPersona) {
          setAvailableRoles([]);
          // In parent persona a staff member has zero staff capabilities; clear the
          // live override so userPermissions resolves to all-false via the null role.
          setActiveRoleCanManageAthletes(null);
          setSendingDisabledForUser(null);
        } else {
          setAvailableRoles(uniqueRoles);
          // In coach persona, override the constant with the live per-org boolean for
          // the active role (issue #978). If the column was somehow absent, leave null
          // to fall back to the constant.
          setActiveRoleCanManageAthletes(
            typeof liveCanManageAthletes === 'boolean' ? liveCanManageAthletes : null
          );
          // U9: any active staff row with sending_disabled hides compose.
          setSendingDisabledForUser(
            (staffData || []).some((row: any) => row?.sending_disabled === true)
          );
        }

        // Create user profile for staff member (with persona-aware overrides)
        const userData = authUserData;
        const personaUserType: UserProfile['userType'] =
          activePersona === 'parent' ? 'parent' : 'staff';
        const userProfile: UserProfile = {
          id: user.id,
          email: user.email || '',
          firstName: (userData && userData.first_name) || '',
          lastName: (userData && userData.last_name) || '',
          avatar_url: (userData && userData.avatar_url) || undefined, // ✅ ADDED: Include avatar URL
          activeRole: isInCoachPersona ? activeRole : null,
          userType: personaUserType,
          availablePersonas: computedPersonas,
          activePersona,
        };

        if (!isCurrent()) return;
        setCurrentUser(userProfile);
        setLoadedOrgId(currentOrganization.id);
        setLoadedUserId(user.id);

        // Save active role to localStorage (always — preserved across persona switches)
        if (activeRole) {
          localStorage.setItem(savedRoleKey, activeRole);
        }
        // Persist the persona we resolved this load with.
        if (activePersona && !didForceStaffPersonaAfterLogin) {
          localStorage.setItem(personaStorageKey(user.id, currentOrganization.id), activePersona);
        }

        logger.info('Staff user profile created:', { userProfile });
        return;
      }

      // No staff roles — fall back to the parent_organizations data we
      // already fetched in parallel above. (No second query needed.)
      logger.info('No staff roles found at this org', {
        userId: user.id,
        organizationId: currentOrganization.id,
        hasParentMembership: hasParentMembershipAtOrg,
      });

      // #3392 billing-only re-entry: the selected org can be one where the
      // caller's parent membership is INACTIVE but they still owe money there.
      // OrganizationContext sets `billingOnly` only from the server route
      // (inactive membership + qualifying open invoices), so honoring the flag
      // here widens nothing else: an inactive membership with no qualifying
      // invoices is never flagged, never enters the org list, and still lands
      // on "No access to this organization" below. Read-only — no
      // parent_organizations write, no repair helper (R12).
      const isBillingOnlyReentry =
        !hasStaffAtOrg &&
        !hasParentMembershipAtOrg &&
        currentOrganization.billingOnly === true;

      if (hasParentMembershipAtOrg || isBillingOnlyReentry) {
        // Get user profile data from auth_users
        const { data: authUserData } = await supabase
          .from('auth_users')
          .select('first_name, last_name, avatar_url') // ✅ ADDED: Include avatar_url
          .eq('id', user.id)
          .single<AuthUserProfileFields>();

        const availableForAccount: DashboardPersona[] = ['parent'];

        // Create user profile for parent
        const userData = authUserData;
        const userProfile: UserProfile = {
          id: user.id,
          email: user.email || '',
          firstName: (userData && userData.first_name) || '',
          lastName: (userData && userData.last_name) || '',
          avatar_url: (userData && userData.avatar_url) || undefined,
          activeRole: null, // Parents don't have org staff roles
          userType: 'parent',
          availablePersonas: availableForAccount,
          activePersona: 'parent',
        };

        if (!isCurrent()) return;
        setCurrentUser(userProfile);
        setAvailableRoles([]); // Parents don't have role switching
        setLoadedOrgId(currentOrganization.id);
        setLoadedUserId(user.id);

        // Persist persona for consistency with the staff branch.
        localStorage.setItem(personaStorageKey(user.id, currentOrganization.id), 'parent');

        logger.info('Parent user profile created:', { userProfile });
        return;
      }

      // Check if user is an athlete (include both active and pending)
      const { data: athleteData, error: athleteError } = await supabase
        .from('athlete_organizations')
        .select(`
          athletes!inner (
            id,
            first_name,
            last_name,
            auth_user_id,
            profile_image_url
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .eq('athletes.auth_user_id', user.id)
        .in('status', ['active', 'pending']);

      if (athleteError) {
        logger.error('Error checking athlete access:', athleteError);
      }

      if (athleteData && athleteData.length > 0) {
        // Use athlete data directly (profile image is in athletes table, not auth_users)
        type AthleteJoinShape = {
          athletes?: {
            id?: string;
            first_name?: string | null;
            last_name?: string | null;
            auth_user_id?: string | null;
            profile_image_url?: string | null;
          };
        };
        const athleteRecord = athleteData[0] as AthleteJoinShape;
        const athleteProfile = athleteRecord?.athletes;

        // Plan 2026-05-23-002 U2: dual-shape resolution. A backfilled adult
        // athlete (U3 cohort) has both an athlete record at this org AND a
        // parents row globally — but no parent_organizations row, so the
        // earlier hasParentMembershipAtOrg branch above won't catch them.
        //
        // PR #544 remediation (ADV-001): the parents-row check MUST be
        // org-scoped. A user with a parents row globally but ONLY a parent
        // role at a DIFFERENT gym (e.g. parent at Gym B, athlete at Gym A)
        // would otherwise be misrouted to the parent dashboard at Gym A
        // where they have no parent_organizations row → stranded with no
        // visible children. Require BOTH (a) a parents row AND (b) a
        // parent_organizations row at the CURRENT org before resolving as
        // parent. The backfill in U3 creates parent_organizations rows at
        // each org where the athlete already has a membership, so the U3
        // cohort still resolves correctly here. Cross-gym users with
        // athlete-only access at this org fall through to the legacy
        // athlete-only path below.
        const { data: parentsRow } = await supabase
          .from('parents')
          .select('id')
          .eq('auth_user_id', user.id)
          .maybeSingle();

        let parentOrgMembershipAtCurrentOrg = false;
        if (parentsRow) {
          const { data: parentOrgRow } = await supabase
            .from('parent_organizations')
            .select('id')
            .eq('parent_id', parentsRow.id)
            .eq('organization_id', currentOrganization.id)
            .maybeSingle();
          parentOrgMembershipAtCurrentOrg = !!parentOrgRow;
        }

        if (parentsRow && parentOrgMembershipAtCurrentOrg) {
          const { data: authUserData } = await supabase
            .from('auth_users')
            .select('first_name, last_name, avatar_url')
            .eq('id', user.id)
            .single<AuthUserProfileFields>();

          const userData = authUserData;
          const userProfile: UserProfile = {
            id: user.id,
            email: user.email || '',
            firstName: (userData && userData.first_name) || athleteProfile?.first_name || '',
            lastName: (userData && userData.last_name) || athleteProfile?.last_name || '',
            avatar_url: (userData && userData.avatar_url) || athleteProfile?.profile_image_url || undefined,
            activeRole: null,
            userType: 'parent',
            availablePersonas: ['parent'],
            activePersona: 'parent',
          };

          if (!isCurrent()) return;
          setCurrentUser(userProfile);
          setAvailableRoles([]);
          setLoadedOrgId(currentOrganization.id);
          setLoadedUserId(user.id);

          localStorage.setItem(personaStorageKey(user.id, currentOrganization.id), 'parent');

          logger.info('Dual-shape adult athlete resolved as parent (post-backfill):', { userProfile });
          return;
        }

        // Athlete path: no parents row, or no parent link at THIS gym. A login
        // that holds its own self row still manages its own account (#5413),
        // so it must not fall into the restricted (dependent teen) path.
        let selfManaged = false;
        if (parentsRow && athleteProfile?.id) {
          const selfManagedMap = await getSelfManagedAthleteIds([athleteProfile.id]);
          if (selfManagedMap) selfManaged = selfManagedMap.get(athleteProfile.id) === true;
          else logger.warn('Self-managed read failed; treating the login as not self-managed');
        }
        const userProfile: UserProfile = {
          id: user.id,
          email: user.email || '',
          firstName: athleteProfile?.first_name || '',
          lastName: athleteProfile?.last_name || '',
          avatar_url: athleteProfile?.profile_image_url || undefined, // ✅ Fixed: Use profile_image_url from athletes table
          activeRole: null, // Athletes don't have org staff roles
          userType: 'athlete',
          isSelfManaged: selfManaged,
          // Athletes have their own dashboard — no persona toggle.
          availablePersonas: [],
          activePersona: null,
        };

        if (!isCurrent()) return;
        setCurrentUser(userProfile);
        setAvailableRoles([]); // Athletes don't have role switching
        setLoadedOrgId(currentOrganization.id);
        setLoadedUserId(user.id);

        logger.info('Athlete user profile created:', { userProfile });
        return;
      }

      // User has no access to this organization
      logger.warn('User has no access to this organization', {
        userId: user.id,
        organizationId: currentOrganization.id
      });
      if (!isCurrent()) return;
      setCurrentUser(null);
      setAvailableRoles([]);
      setLoadedOrgId(currentOrganization.id);
      setLoadedUserId(user.id);
      setError('No access to this organization');
      // Sets loadedOrgId, so isRolesStale will NOT hold this at 'loading' —
      // flag it explicitly or a null-user viewer reads as allowed.
      setResolutionFailed(true);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      logger.error('Error in fetchUserRoles:', errorMessage);
      if (!isCurrent()) return;
      setError(errorMessage);
      // Role resolution failed for this user+org. Do NOT leave a previously
      // resolved profile in place — a stale currentUser from a prior org can
      // render another org's staff identity on gated pages (e.g. the billing
      // access gate). Clearing it marks the role as unresolved so those pages
      // can show a recoverable "we couldn't confirm your role" state instead of
      // a wrong lockout. Only the failure path is affected; successful branches
      // above still set currentUser normally.
      setCurrentUser(null);
      setAvailableRoles([]);
      // Flag it, don't just clear it. isRolesStale usually parks this state at
      // 'loading', but not when the org was ALREADY loaded (a persona switch
      // re-fetches the same org) — there roleResolution would read 'resolved'
      // with a null user, which useSelfServiceAccess treats as allowed. Same
      // invariant as the org-less path: an unknown answer blocks the action.
      setResolutionFailed(true);
      // Record the failed answer as given (#5726). Without this a FIRST load
      // that fails leaves isRolesStale true, so the page shows "Checking your
      // account..." for good instead of the retry. The flag keeps the
      // already-loaded skip from swallowing a later retry.
      setLoadedOrgId(currentOrganization.id);
      setLoadedUserId(user.id);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  // Set active role function
  const setActiveRole = (role: OrgRole) => {
    if (!currentUser || !availableRoles.includes(role)) {
      logger.warn('Cannot set active role:', { role, availableRoles });
      return;
    }

    logger.info('Setting active role:', role);
    
    const updatedUser = { ...currentUser, activeRole: role };
    setCurrentUser(updatedUser);

    // Save to localStorage
    if (user && currentOrganization) {
      const savedRoleKey = `activeRole_${user.id}_${currentOrganization.id}`;
      localStorage.setItem(savedRoleKey, role);
    }
  };

  // Coach + Parent persona toggle: switch between coach and parent dashboards
  // for the currently-selected org. Validates the persona is actually
  // available for this user+org pair, then refetches roles so all derived
  // state (userType, activeRole, availableRoles, userPermissions) is rebuilt
  // consistently. Persistence happens in fetchUserRoles via personaStorageKey.
  const setActivePersona = (persona: DashboardPersona, options?: { redirectTo?: string }) => {
    if (!currentUser || !currentOrganization || !user) {
      logger.warn('Cannot set persona: missing user or organization context');
      return;
    }
    if (!currentUser.availablePersonas.includes(persona)) {
      logger.warn('Cannot set persona — not available for this user/org', {
        persona,
        availablePersonas: currentUser.availablePersonas,
      });
      return;
    }
    if (currentUser.activePersona === persona) {
      // No-op — already in this persona.
      return;
    }

    logger.info('Switching dashboard persona', {
      from: currentUser.activePersona,
      to: persona,
      organizationId: currentOrganization.id,
    });

    // Persist first so the next fetchUserRoles run picks up the new value.
    localStorage.setItem(personaStorageKey(user.id, currentOrganization.id), persona);

    // Reset loadedOrgId so the dedupe guard inside fetchUserRoles allows the
    // refetch (same-org early-return is keyed on loadedOrgId), and bump the
    // persona refresh tick so the effect actually re-fires (loadedOrgId is
    // not in the dep array; we need the tick to retrigger).
    setLoadedOrgId(null);
    setLoadedUserId(null);
    setPersonaRefreshTick(t => t + 1);

    // Route to the appropriate dashboard root for the new persona. This
    // prevents Access Denied flashes when toggling from a persona-specific
    // deep link (e.g. coach is on /dashboard/teams/123 and switches to
    // parent — staying on that URL would render an access-denied guard).
    // /dashboard auto-redirects to /dashboard/parent when userType=parent
    // and /dashboard/parent auto-redirects to /dashboard when userType flips
    // back to staff, so pushing the explicit destination is safe and avoids
    // a double-redirect.
    const targetPath =
      options?.redirectTo ?? (persona === 'parent' ? '/dashboard/parent' : '/dashboard');
    router.push(targetPath);
  };

  // Fetch roles when user or organization changes — or when persona is
  // explicitly switched (personaRefreshTick).
  useEffect(() => {
    if (!authLoading && !orgLoading) {
      // The org-scoped path has its own try/finally, but the org-less path above
      // it does not — it runs before `try {` opens. A transport-level throw there
      // (dropped connection, aborted fetch) would reject this promise with
      // isLoading still true, i.e. the permanent "Checking your account..."
      // spinner, reached by a different door than the one this file just closed.
      // Never leave the spinner up on a throw.
      //
      // Every state write in fetchUserRoles is gated on its generation token so
      // a superseded run cannot clobber a newer one; this catch has to honour
      // the same discipline. fetchUserRoles bumps the token synchronously as it
      // starts, so reading it straight after the call gives THIS run's
      // generation — without it, a late rejection from an abandoned run would
      // stamp an error onto a profile that resolved fine (the billing page
      // reads userRoleError to decide "we couldn't confirm your role") and
      // could drop the spinner while a newer fetch is still in flight, briefly
      // rendering the enroll page with the client-only staff guard not yet
      // resolved.
      //
      // Deliberately does NOT clear currentUser: the org-scoped path already
      // clears it on its own failures, where the stale cross-org identity risk
      // actually lives.
      const run = fetchUserRoles();
      const generation = fetchRolesGenerationRef.current;
      const orgId = currentOrganization?.id ?? null;
      const userId = user?.id ?? null;
      run.catch((err) => {
        if (fetchRolesGenerationRef.current !== generation) return;
        logger.error('Role resolution threw — releasing the loading gate', err);
        setError(err instanceof Error ? err.message : 'We could not confirm your account');
        // Mark it unresolved rather than leaving a half-state behind. Every
        // staff check is `currentUser?.userType === 'staff'`, which null
        // PASSES — so leaving a null user here would walk an unconfirmed
        // viewer straight onto the enrollment form. Safe to clear now that the
        // generation guard above rules out clobbering a newer run.
        setCurrentUser(null);
        setAvailableRoles([]);
        setResolutionFailed(true);
        // Mark the gym-less answer as given, or isRolesStale would hold this
        // viewer on 'loading' forever instead of showing the retry.
        if (!orgId) {
          setLoadedOrgId(null);
          setLoadedUserId(userId);
        }
        setIsLoading(false);
      });
    }
  // #3392: `currentOrganization?.billingOnly` is a dependency because the
  // mobile webview first selects a minimal org stub (no flag) and hydrates the
  // flagged row moments later — same org id, so without the flag in the deps a
  // failed first resolution would stand until a manual retry. For active orgs
  // the flag never changes (undefined), so this adds no extra runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentOrganization?.id, currentOrganization?.billingOnly, authLoading, orgLoading, isSwitchingOrganization, personaRefreshTick]);

  // Calculate current permissions. Issue #978: override can_manage_athletes with the
  // LIVE per-org org_staff_roles boolean when we have it, so the UI's read-only/edit
  // decision agrees with RLS per-org. The rest of DEFAULT_ORG_PERMISSIONS still backs
  // the other flags (a scoped override, not a full DB-sourced permissions migration).
  // null override → keep the constant (non-staff, org-less staff, or unresolved role).
  const baseUserPermissions = getUserPermissions(currentUser?.activeRole || null);
  const permissionsWithAthleteOverride: UserPermissions =
    activeRoleCanManageAthletes === null
      ? baseUserPermissions
      : { ...baseUserPermissions, can_manage_athletes: activeRoleCanManageAthletes };
  // U9 (R19): a revoked sender loses compose in the UI regardless of role.
  const userPermissions: UserPermissions =
    sendingDisabledForUser === true
      ? { ...permissionsWithAthleteOverride, can_send_notifications: false }
      : permissionsWithAthleteOverride;

  // Consider context loading if:
  // 1. Internal fetch state is loading
  // 2. Auth context is loading
  // 3. Organization context is loading
  // 4. We have an organization selected but haven't loaded its roles yet (prevents stale access denied)
  // 5. User is signed in but role hasn't been resolved yet (prevents staff bypass on
  //    enrollment pages during the render gap between sign-in and first fetchUserRoles)
  // 6. No gym is selected and the answer we hold is not a gym-less answer for
  //    THIS user. A sign-in on an open page sets the new user a render before
  //    anything here reloads; the old answer must not read as this viewer's role.
  const isRolesStale = currentOrganization?.id
    ? loadedOrgId !== currentOrganization.id || loadedUserId !== user?.id
    : !!user &&
      (loadedUserId !== user.id || loadedOrgId !== null);
  const isContextLoading = isLoading || authLoading || orgLoading || isRolesStale;
  const roleResolution: RoleResolution = isContextLoading
    ? 'loading'
    : resolutionFailed
      ? 'unconfirmed'
      : 'resolved';

  const value = {
    currentUser,
    availableRoles,
    userPermissions,
    setActiveRole,
    setActivePersona,
    isLoading: isContextLoading,
    error,
    roleResolution,
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

// Custom hook to use user role context
export function useUserRole() {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
}
